import { useState, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../lib/AuthContext';
import { useAgency } from '../lib/AgencyContext';
import { useI18n } from '../lib/i18n';
import Icon from './Icon';
import ImportAgencies from './ImportAgencies';
import OnboardingPage from './OnboardingPage';

// Pretraga bez obzira na velika/mala slova i kvačice (č/ć→c, š→s, ž→z, đ→dj)
const norm = (s) => String(s || '')
  .toLowerCase()
  .replace(/đ/g, 'dj')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '');

const collator = new Intl.Collator('sr-Latn', { sensitivity: 'base' });

const initials = (name) => String(name || '?')
  .trim()
  .split(/\s+/)
  .slice(0, 2)
  .map((w) => w[0])
  .join('')
  .toUpperCase();

export default function AgencyPicker() {
  const { user, signOut } = useAuth();
  const { agencies, loading, selectAgency, createAgency, refreshAgencies, isAdmin, canAddAgency } = useAgency();
  // "Dodaj novu firmu" otvara isti formular kao pri prvom ulasku
  const [newFirm, setNewFirm] = useState(false);
  const { t, tr } = useI18n();
  const [query, setQuery] = useState('');
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);
  const [starBusy, setStarBusy] = useState(false);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showTools, setShowTools] = useState(false);

  // Brisanje naloga: korak 1 (upozorenje + broj podataka), korak 2 (upiši naziv)
  const [delTarget, setDelTarget] = useState(null);
  const [delStep, setDelStep] = useState(1);
  const [delCounts, setDelCounts] = useState(null);
  const [delTyped, setDelTyped] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [delError, setDelError] = useState('');

  const openDelete = async (a) => {
    setDelTarget(a); setDelStep(1); setDelTyped(''); setDelError(''); setDelCounts(null);
    const [inv, cust] = await Promise.all([
      supabase.from('invoices').select('id', { count: 'exact', head: true }).eq('agency_id', a.id),
      supabase.from('customers').select('id', { count: 'exact', head: true }).eq('agency_id', a.id),
    ]);
    setDelCounts({ invoices: inv.count || 0, customers: cust.count || 0 });
  };
  const closeDelete = () => { if (!deleting) setDelTarget(null); };
  // Poređenje bez obzira na razmake (i "nevidljive"), velika/mala slova i kvačice
  const looseName = (v) => norm(v).replace(/\s+/g, ' ').trim();
  const nameMatches = delTarget && looseName(delTyped) !== '' && looseName(delTyped) === looseName(delTarget.name);

  const confirmDelete = async () => {
    if (!nameMatches) return;
    setDeleting(true);
    setDelError('');
    const id = delTarget.id;
    // Prvo podaci naloga, pa sam nalog
    for (const table of ['invoices', 'tax_payments', 'customers', 'actors', 'agency_profile']) {
      const { error: e } = await supabase.from(table).delete().eq('agency_id', id);
      if (e && !/does not exist|schema cache/i.test(e.message)) {
        setDeleting(false);
        setDelError(`Brisanje nije uspelo (${table}): ${e.message}`);
        return;
      }
    }
    const { error: e2 } = await supabase.from('agencies').delete().eq('id', id);
    setDeleting(false);
    if (e2) { setDelError(e2.message); return; }
    setDelTarget(null);
    refreshAgencies();
  };

  const startRename = (a) => { setError(''); setEditingId(a.id); setEditName(a.name || ''); };
  const cancelRename = () => { setEditingId(null); setEditName(''); };
  const saveRename = async () => {
    const name = editName.trim();
    if (!name) return;
    setRenaming(true);
    const { error: err } = await supabase.from('agencies').update({ name }).eq('id', editingId);
    setRenaming(false);
    if (err) {
      setError(err.message);
      return;
    }
    cancelRename();
    refreshAgencies();
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setSaving(true);
    const created = await createAgency(newName.trim());
    setSaving(false);
    if (created) {
      setNewName('');
      setAdding(false);
      selectAgency(created.id);
    }
  };

  // Označi/ukloni "moju agenciju" — samo jedna može biti označena
  const togglePrimary = async (agency) => {
    setError('');
    setStarBusy(true);
    const makePrimary = !agency.is_primary;
    let err = null;
    if (makePrimary) {
      const r1 = await supabase.from('agencies').update({ is_primary: false })
        .eq('owner_user_id', user.id).neq('id', agency.id);
      err = r1.error;
    }
    if (!err) {
      const r2 = await supabase.from('agencies').update({ is_primary: makePrimary }).eq('id', agency.id);
      err = r2.error;
    }
    setStarBusy(false);
    if (err) {
      setError(err.message);
      return;
    }
    refreshAgencies();
  };

  const primary = agencies.find((a) => a.is_primary) || null;

  // Ostale agencije po abecedi (srpski redosled), filtrirane pretragom
  const q = norm(query.trim());
  const matches = (a) => !q || norm(a.name).includes(q);
  const showPrimary = primary && matches(primary);
  const others = useMemo(
    () => agencies
      .filter((a) => !a.is_primary)
      .filter((a) => !q || norm(a.name).includes(q))
      .sort((a, b) => collator.compare(a.name || '', b.name || '')),
    [agencies, q],
  );

  // Grupisanje po početnom slovu (A, B, C, Č...)
  const groups = useMemo(() => {
    const out = [];
    others.forEach((a) => {
      const letter = (String(a.name || '#').trim()[0] || '#').toUpperCase();
      const last = out[out.length - 1];
      if (last && last.letter === letter) last.items.push(a);
      else out.push({ letter, items: [a] });
    });
    return out;
  }, [others]);

  const results = [...(showPrimary ? [primary] : []), ...others];

  const onSearchKey = (e) => {
    if (e.key === 'Enter' && results.length === 1) selectAgency(results[0].id);
    if (e.key === 'Escape') setQuery('');
  };

  if (loading) return <div style={styles.loadingPage}>{t('loadingGeneric')}</div>;

  const renderRow = (a, highlight = false) => {
    if (editingId === a.id) {
      return (
        <div key={a.id} style={{ ...styles.row, ...(highlight ? styles.rowPrimary : {}), cursor: 'default' }}>
          <span style={{ ...styles.avatar, ...(highlight ? styles.avatarPrimary : {}) }}>{initials(editName || a.name)}</span>
          <input
            autoFocus
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') saveRename(); if (e.key === 'Escape') cancelRename(); }}
            style={styles.renameInput}
          />
          <button type="button" onClick={cancelRename} disabled={renaming} style={styles.renameCancel}>{t('cancelBtn')}</button>
          <button type="button" onClick={saveRename} disabled={renaming || !editName.trim()} style={styles.renameSave}>
            {renaming ? '...' : t('saveBtnShort')}
          </button>
        </div>
      );
    }
    return (
    <div
      key={a.id}
      role="button"
      tabIndex={0}
      onClick={() => selectAgency(a.id)}
      onKeyDown={(e) => { if (e.key === 'Enter') selectAgency(a.id); }}
      style={{ ...styles.row, ...(highlight ? styles.rowPrimary : {}) }}
    >
      <span style={{ ...styles.avatar, ...(highlight ? styles.avatarPrimary : {}) }}>{initials(a.name)}</span>
      <span style={styles.rowName}>{a.name}</span>
      <button
        type="button"
        title={t('renameAgency')}
        aria-label={t('renameAgency')}
        onClick={(e) => { e.stopPropagation(); startRename(a); }}
        style={styles.iconBtn}
      ><Icon name="pencil" size={16} /></button>
      <button
        type="button"
        title={tr('Obriši nalog')}
        aria-label={`Obriši nalog ${a.name}`}
        onClick={(e) => { e.stopPropagation(); openDelete(a); }}
        style={{ ...styles.iconBtn, color: 'var(--ink-300)' }}
        className="agency-delete-btn"
      ><Icon name="trash" size={16} /></button>
      <button
        type="button"
        title={a.is_primary ? t('unmarkMine') : t('markAsMine')}
        aria-label={a.is_primary ? t('unmarkMine') : t('markAsMine')}
        disabled={starBusy}
        onClick={(e) => { e.stopPropagation(); togglePrimary(a); }}
        style={styles.iconBtn}
      >{a.is_primary
        ? <Icon name="star" size={19} color="var(--gold-500)" filled strokeWidth={1.5} />
        : <Icon name="star" size={18} strokeWidth={1.6} />}</button>
    </div>
    );
  };

  if (newFirm) return <OnboardingPage mode="add" onCancel={() => setNewFirm(false)} />;

  return (
    <div style={styles.page}>
      <div style={styles.topRow}>
        <span style={styles.brandRow}>
          <span style={styles.logoBadge}>FinArt<br />Studio</span>
          <span style={styles.brand}>FinArt <span style={{ fontWeight: 400 }}>Studio</span></span>
        </span>
        <button onClick={signOut} style={styles.logoutBtn}>
          <Icon name="logout" size={16} />
          {t('logoutBtn')}
        </button>
      </div>

      <div style={styles.wrap}>
        <div style={styles.headRow}>
          <div>
            <h1 style={styles.heading}>{t('yourAgenciesHeading')}</h1>
            <p style={styles.sub}>{user?.email} · {t('agenciesCount', agencies.length)}</p>
          </div>
        </div>

        {adding && (
          <form onSubmit={handleCreate} style={styles.addForm}>
            <input
              autoFocus
              placeholder={t('agencyNamePlaceholder')}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              style={styles.addInput}
            />
            <button type="button" onClick={() => { setAdding(false); setNewName(''); }} style={styles.addCancel}>{t('cancelBtn')}</button>
            <button type="submit" disabled={!newName.trim() || saving} style={styles.addConfirm}>
              {saving ? '...' : t('saveBtnShort')}
            </button>
          </form>
        )}

        {/* Jedan red: pretraga · Dodaj novu firmu · ⋯ (alati samo za administratora) */}
        <div style={styles.toolbar}>
        {agencies.length > 0 && (
          <label style={styles.searchBox}>
            <Icon name="search" size={16} color="var(--ink-500)" strokeWidth={1.9} />
            <input
              autoFocus={!adding && window.matchMedia('(hover: hover) and (pointer: fine)').matches}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onSearchKey}
              placeholder={tr('Pretraži po imenu…', 'Search by name…', 'Поиск по имени…')}
              aria-label={tr('Pretraži po imenu', 'Search by name', 'Поиск по имени')}
              style={styles.searchInput}
            />
            {results.length === 1 && q && <span style={styles.enterHint}>Enter</span>}
          </label>
        )}
          {!adding && canAddAgency && (
            <button onClick={() => setNewFirm(true)} style={styles.addBtn}>
              <Icon name="plus" size={15} strokeWidth={2.2} />
              {tr('Dodaj novu firmu', 'Add a company', 'Добавить фирму')}
            </button>
          )}
          {!adding && isAdmin && (
            <div style={{ position: 'relative' }}>
              <button onClick={() => setShowTools((o) => !o)} style={styles.toolsBtn} aria-label={tr('Još opcija', 'More options', 'Ещё')} aria-expanded={showTools}>
                <Icon name="moreVertical" size={16} />
              </button>
              {showTools && (
                <>
                  <div style={styles.toolsBackdrop} onClick={() => setShowTools(false)} />
                  <div style={styles.toolsMenu} role="menu">
                    <button role="menuitem" style={styles.toolsItem} onClick={() => { setShowTools(false); setShowImport(true); }}>
                      <Icon name="download" size={15} style={{ transform: 'rotate(180deg)' }} />
                      {tr('Uvezi iz Excela', 'Import from Excel', 'Импорт из Excel')}
                    </button>
                    <button role="menuitem" style={styles.toolsItem} onClick={() => { setShowTools(false); setAdding(true); }}>
                      <Icon name="plus" size={15} strokeWidth={2.2} />
                      {tr('Brzo dodaj (samo ime)', 'Quick add (name only)', 'Быстро (только имя)')}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {!canAddAgency && (
          <p style={styles.tip}>
            {tr('Za dodavanje još jedne firme pišite na podrska@finart.rs.',
              'To add another company, write to podrska@finart.rs.',
              'Чтобы добавить ещё фирму, напишите на podrska@finart.rs.')}
          </p>
        )}

        {error && <div style={styles.errorBox}>{error}</div>}

        {showPrimary && (
          <div style={styles.section}>
            <div style={styles.sectionLabel}>
              <Icon name="star" size={13} color="var(--gold-500)" filled strokeWidth={1.5} />
              {t('myAgencyLabel')}
            </div>
            {renderRow(primary, true)}
          </div>
        )}

        {groups.map((g) => (
          <div key={g.letter} style={styles.section}>
            <div style={styles.letter}>{g.letter}</div>
            <div style={styles.list}>
              {g.items.map((a) => renderRow(a))}
            </div>
          </div>
        ))}

        {agencies.length > 0 && results.length === 0 && (
          <p style={styles.emptyHint}>{t('noSearchResults')}</p>
        )}
        {agencies.length === 0 && !adding && (
          <p style={styles.emptyHint}>{t('noAgenciesYet')}</p>
        )}
        {agencies.length > 1 && !primary && !q && (
          <p style={styles.tip}>{t('starTip')}</p>
        )}
      </div>
      {showImport && <ImportAgencies onClose={() => setShowImport(false)} />}
      {delTarget && (
        <div style={styles.overlay} onClick={closeDelete}>
          <div style={styles.modal} role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalIcon}><Icon name="trash" size={20} color="var(--red-600)" /></div>
            {delStep === 1 ? (
              <>
                <div style={styles.modalTitle}>Obrisati nalog „{delTarget.name}"?</div>
                <div style={styles.modalText}>
                  Trajno se brišu i svi podaci ovog naloga:
                  {delCounts
                    ? <> <strong>{delCounts.invoices}</strong> {delCounts.invoices === 1 ? 'dokument' : 'dokumenata'} (fakture, profakture, fiskalni računi), <strong>{delCounts.customers}</strong> {delCounts.customers === 1 ? 'klijent' : 'klijenata'}, podaci o firmi, KPO knjiga i evidencija poreza.</>
                    : ' učitavam...'}
                </div>
                {delCounts && delCounts.invoices > 0 && (
                  <div style={styles.dangerNote}>Ovaj nalog ima izdate dokumente. Ako su to stvarne fakture, preporuka je da ih prvo sačuvate kao PDF.</div>
                )}
              </>
            ) : (
              <>
                <div style={styles.modalTitle}>Poslednja provera</div>
                <div style={styles.modalText}>Brisanje je trajno i ne može se poništiti. Da potvrdite, upišite naziv naloga:</div>
                <div style={styles.nameHint}>{delTarget.name}</div>
                <input
                  autoFocus
                  value={delTyped}
                  onChange={(e) => setDelTyped(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') confirmDelete(); }}
                  placeholder="Upišite naziv naloga"
                  aria-label="Naziv naloga za potvrdu brisanja"
                  style={styles.confirmInput}
                />
              </>
            )}
            {delError && <div style={styles.errorBox}>{delError}</div>}
            <div style={styles.modalRow}>
              <button onClick={closeDelete} disabled={deleting} style={styles.renameCancel}>{t('cancelBtn')}</button>
              {delStep === 1 ? (
                <button onClick={() => setDelStep(2)} disabled={!delCounts} style={styles.renameSave}>Nastavi</button>
              ) : (
                <button onClick={confirmDelete} disabled={!nameMatches || deleting}
                  style={{ ...styles.dangerBtn, opacity: nameMatches && !deleting ? 1 : 0.45 }}>
                  {deleting ? 'Brisanje...' : 'Trajno obriši nalog'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      <style>{`.agency-delete-btn:hover { color: var(--red-600) !important; background: var(--red-100) !important; }`}</style>
    </div>
  );
}

const styles = {
  loadingPage: { padding: 60, textAlign: 'center', color: 'var(--ink-500)' },
  page: { minHeight: '100vh', background: 'var(--paper)' },
  topRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '16px 24px', borderBottom: '1px solid var(--line)', background: 'var(--surface)',
  },
  brandRow: { display: 'flex', alignItems: 'center', gap: 10 },
  logoBadge: {
    width: 34, height: 34, borderRadius: '50%', background: 'var(--teal-800)', color: 'var(--lavender-300)', textAlign: 'center',
    display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 7, lineHeight: 1.05,
  },
  brand: { fontWeight: 700, fontSize: 18, letterSpacing: '-1px', color: 'var(--ink-900)' },
  logoutBtn: {
    display: 'flex', alignItems: 'center', gap: 8, minHeight: 40,
    border: '1px solid var(--line)', background: 'var(--surface)', borderRadius: 10,
    padding: '0 14px', fontWeight: 600, fontSize: 13, color: 'var(--ink-700)',
  },
  wrap: { maxWidth: 720, margin: '0 auto', padding: '36px 20px 60px' },
  headRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap', marginBottom: 18 },
  heading: { fontSize: 22, marginBottom: 4 },
  sub: { fontSize: 13, color: 'var(--ink-500)', margin: 0 },
  adminRow: { display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap', margin: '-6px 0 14px' },
  adminBtn: {
    display: 'flex', alignItems: 'center', gap: 6, minHeight: 36, padding: '0 12px', borderRadius: 10,
    border: '1px solid var(--line)', background: 'var(--surface)', color: 'var(--ink-700)', fontSize: 13, fontWeight: 600, cursor: 'pointer',
  },
  addBtn: {
    display: 'flex', alignItems: 'center', gap: 7, height: 38, flexShrink: 0, whiteSpace: 'nowrap',
    border: 'none', background: 'var(--teal-800)', color: '#fff',
    borderRadius: 8, padding: '0 14px', fontWeight: 600, fontSize: 13,
  },
  importBtn: {
    display: 'flex', alignItems: 'center', gap: 8, minHeight: 44,
    border: '1px solid var(--line)', background: 'var(--surface)', color: 'var(--teal-800)',
    borderRadius: 8, padding: '0 14px', fontWeight: 700, fontSize: 14,
  },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(12,37,48,0.45)', zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 },
  modal: { background: 'var(--surface)', borderRadius: 10, padding: 24, width: '100%', maxWidth: 440, boxShadow: 'var(--shadow-lg)' },
  modalIcon: { width: 44, height: 44, borderRadius: 8, background: 'var(--red-100)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 },
  modalTitle: { fontSize: 17, fontWeight: 800, color: 'var(--ink-900)', marginBottom: 8 },
  modalText: { fontSize: 14, lineHeight: 1.55, color: 'var(--ink-900)', marginBottom: 14 },
  dangerNote: { background: 'var(--gold-100)', color: 'var(--gold-600)', borderRadius: 10, padding: '8px 12px', fontSize: 12, marginBottom: 14 },
  nameHint: { fontSize: 14, fontWeight: 800, color: 'var(--red-600)', marginBottom: 8, userSelect: 'none' },
  confirmInput: { width: '100%', border: '1.5px solid var(--line)', borderRadius: 10, padding: '11px 13px', fontSize: 15, marginBottom: 14 },
  modalRow: { display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' },
  dangerBtn: { border: 'none', background: 'var(--red-600)', color: '#fff', borderRadius: 8, padding: '9px 16px', fontWeight: 700, fontSize: 13 },
  addForm: {
    display: 'flex', gap: 8, background: 'var(--surface)', border: '1.5px solid var(--line)',
    borderRadius: 10, padding: 12, marginBottom: 14, flexWrap: 'wrap',
  },
  addInput: { flex: '1 1 220px', border: '1.5px solid var(--line)', borderRadius: 10, padding: '10px 12px', fontSize: 14 },
  addCancel: {
    border: '1px solid var(--line)', background: 'var(--surface)', borderRadius: 10,
    padding: '9px 14px', fontWeight: 600, fontSize: 13, color: 'var(--ink-700)',
  },
  addConfirm: {
    border: 'none', background: 'var(--teal-800)', color: '#fff', borderRadius: 10,
    padding: '9px 16px', fontWeight: 700, fontSize: 13,
  },
  toolbar: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 },
  searchBox: {
    flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 8, background: 'var(--surface)',
    border: '1px solid var(--line)', borderRadius: 8, padding: '0 12px', height: 38,
  },
  searchInput: { flex: 1, minWidth: 0, border: 'none', outline: 'none', background: 'transparent', fontSize: 14 },
  toolsBtn: { width: 34, height: 34, border: '1px solid var(--line)', borderRadius: 8, background: 'var(--surface)', color: 'var(--ink-700)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' },
  toolsBackdrop: { position: 'fixed', inset: 0, zIndex: 40 },
  toolsMenu: { position: 'absolute', right: 0, top: 'calc(100% + 6px)', zIndex: 41, width: 230, background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 10, boxShadow: 'var(--shadow-lg)', padding: 6 },
  toolsItem: { display: 'flex', alignItems: 'center', gap: 10, width: '100%', minHeight: 38, border: 'none', background: 'transparent', borderRadius: 8, padding: '0 10px', fontSize: 13, fontWeight: 500, color: 'var(--ink-900)', textAlign: 'left', cursor: 'pointer' },
  enterHint: { fontSize: 11, fontWeight: 600, color: 'var(--ink-500)', border: '1px solid var(--line)', borderRadius: 6, padding: '2px 6px' },
  errorBox: { background: 'var(--red-100)', color: 'var(--red-600)', borderRadius: 10, padding: '8px 12px', fontSize: 13, marginBottom: 12 },
  section: { marginBottom: 14 },
  sectionLabel: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700, color: 'var(--gold-600)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 },
  letter: { fontSize: 12, fontWeight: 700, color: 'var(--ink-500)', padding: '0 4px', marginBottom: 6 },
  list: { display: 'flex', flexDirection: 'column', gap: 6 },
  row: {
    display: 'flex', alignItems: 'center', gap: 14, background: 'var(--surface)', border: '1px solid var(--line)',
    borderRadius: 10, padding: '8px 10px 8px 14px', cursor: 'pointer',
  },
  rowPrimary: { borderColor: 'var(--gold-300)', borderWidth: 1.5, padding: '12px 10px 12px 14px' },
  avatar: {
    width: 36, height: 36, borderRadius: 10, background: 'var(--teal-100)', color: 'var(--teal-800)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0,
  },
  avatarPrimary: { background: 'var(--teal-800)', color: '#fff', width: 42, height: 42, borderRadius: 8, fontSize: 14 },
  rowName: { flex: 1, fontSize: 14, fontWeight: 600, color: 'var(--ink-900)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  iconBtn: {
    width: 40, height: 40, border: 'none', background: 'transparent', borderRadius: 10, color: 'var(--ink-300)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0,
  },
  renameInput: { flex: 1, minWidth: 0, border: '1.5px solid var(--teal-700)', borderRadius: 8, padding: '7px 10px', fontSize: 14, fontWeight: 600 },
  renameCancel: { border: '1px solid var(--line)', background: 'var(--surface)', borderRadius: 8, padding: '7px 12px', fontWeight: 600, fontSize: 12, color: 'var(--ink-700)', flexShrink: 0 },
  renameSave: { border: 'none', background: 'var(--teal-800)', color: '#fff', borderRadius: 8, padding: '7px 14px', fontWeight: 700, fontSize: 12, flexShrink: 0 },
  emptyHint: { fontSize: 13, color: 'var(--ink-500)', marginTop: 20, textAlign: 'center' },
  tip: { fontSize: 12, color: 'var(--ink-500)', marginTop: 18, textAlign: 'center' },
};
